import { useEffect, useRef } from 'react';
import { mono, dmMono, secondaryButtonStyle } from './tokens';

const TONE_COLORS = {
  destructive: {
    accent: '#fca5a5',
    border: 'rgba(248,113,113,0.32)',
    buttonBg: 'rgba(248,113,113,0.14)',
    buttonBorder: 'rgba(248,113,113,0.5)',
    buttonColor: '#fecaca',
    buttonHoverBg: 'rgba(248,113,113,0.25)',
    buttonHoverColor: '#fff5f5',
  },
  neutral: {
    accent: 'rgba(255,255,255,0.85)',
    border: 'rgba(255,255,255,0.12)',
    buttonBg: 'rgba(255,255,255,0.08)',
    buttonBorder: 'rgba(255,255,255,0.18)',
    buttonColor: 'white',
    buttonHoverBg: 'rgba(255,255,255,0.14)',
    buttonHoverColor: 'white',
  },
};

export default function ConfirmModal({
  open,
  title,
  description,
  errorMessage,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'destructive',
  onConfirm,
  onCancel,
}) {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter') onConfirm?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  const palette = TONE_COLORS[tone] || TONE_COLORS.destructive;

  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 250,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <style>{`
        @keyframes confirmModalIn {
          from { opacity: 0; transform: translateY(4px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      />
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: 'min(420px, calc(100vw - 32px))',
          borderRadius: 12,
          background: 'rgba(8,10,16,0.96)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: `1px solid ${palette.border}`,
          boxShadow: '0 28px 72px rgba(0,0,0,0.55)',
          padding: 20,
          display: 'grid',
          gap: 14,
          animation: 'confirmModalIn 0.18s cubic-bezier(0.22,1,0.36,1)',
        }}
      >
        <div
          style={{
            ...mono,
            fontSize: 15,
            fontWeight: 600,
            color: 'rgba(255,245,245,0.96)',
            lineHeight: 1.3,
          }}
        >
          {title}
        </div>
        {description ? (
          <div
            style={{
              ...mono,
              fontSize: 12,
              lineHeight: 1.6,
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            {description}
          </div>
        ) : null}
        {errorMessage ? (
          <div
            role="alert"
            style={{
              ...mono,
              borderRadius: 8,
              border: '1px solid rgba(248,113,113,0.34)',
              background: 'rgba(127,29,29,0.2)',
              color: '#fecaca',
              fontSize: 11,
              lineHeight: 1.55,
              padding: '10px 12px',
              wordBreak: 'break-word',
            }}
          >
            {errorMessage}
          </div>
        ) : null}
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            marginTop: 4,
          }}
        >
          <button
            ref={cancelRef}
            onClick={onCancel}
            style={{
              ...secondaryButtonStyle,
              minWidth: 84,
            }}
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            style={{
              height: 26,
              padding: '0 14px',
              minWidth: 120,
              borderRadius: 6,
              background: palette.buttonBg,
              border: `1px solid ${palette.buttonBorder}`,
              color: palette.buttonColor,
              ...dmMono,
              fontSize: 9,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'background 0.12s, color 0.12s, border-color 0.12s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = palette.buttonHoverBg;
              e.currentTarget.style.color = palette.buttonHoverColor;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = palette.buttonBg;
              e.currentTarget.style.color = palette.buttonColor;
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
